import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

/**
 * react-slick ships behaviour but no CSS; the stylesheets live in the separate
 * `slick-carousel` package. Until now the app never depended on it and pulled
 * both sheets off cdnjs from two <link> tags in index.html — a third party on
 * the critical render path, unpinned by any lockfile, and the only reason the
 * carousel had layout at all. They are a real dependency now, imported here
 * because this module is what both sliders already share.
 *
 * The font and ajax-loader.gif that slick-theme references resolve inside the
 * package, so Vite emits them as assets instead of fetching them from the CDN.
 */
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/**
 * Replaces carousel.scss (41 lines), which redrew react-slick's arrows as CSS
 * chevrons over the slick-theme stylesheet.
 *
 * The sheet emitted bare `.slick-*` selectors, so it applied to every slider in
 * the app — this Carousel and the mobile branch of WelcomeRebelOverlay, which
 * leaves `arrows` at its default of true. Both apply this, so nothing changes.
 * The `!important`s beat slick-theme's own rules and are kept for that reason.
 *
 * The chevron drew itself in flat black on a black wash; both now come from the
 * palette, so the arrows follow the theme rather than sitting outside it.
 */
export const slickArrowsSx: SystemStyleObject<Theme> = {
  "& .slick-arrow": {
    height: "100%",
    width: "3rem",
    zIndex: 9,
    "&::before": {
      display: "inline-block",
      height: 20,
      width: 20,
      borderTop: "2px solid",
      borderRight: "2px solid",
      borderColor: "secondary.main",
      content: '""',
    },
    /* 725px is the theme's `md`; spelled out because this object is spread
       into a caller's sx rather than built from a theme. */
    "@media screen and (min-width: 725px)": {
      background: (theme: Theme) =>
        `${alpha(theme.palette.secondary.main, 0.15)} !important`,
    },
  },
  "& .slick-prev": {
    left: "0 !important",
    "&::before": { transform: "rotate(-135deg)" },
  },
  "& .slick-next": {
    right: "0 !important",
    "&::before": { transform: "rotate(45deg)" },
  },
  "& .slick-disabled": { display: "none !important" },
};
